/**
 * forecastSuggestions.ts — Forecast programme F3 (register row 71): the
 * Suggested categories card's MODEL, pure. ForecastSuggestions.tsx renders
 * exactly what this returns.
 *
 * Spec: docs/design/calm-minimal/specs/klaxiora-forecast-spec.md §3.3, under
 * docs/design/stier/ADOPTION.md §18.6 and §18.10 step 4:
 *
 * - P2-9 STANDS. A suggestion is never a category. The card shows the
 *   server's suggestion beside its reason and a human accepts ONE deal at a
 *   time; the accept is the deal's own PUT (owner/admin, refused on a closed
 *   deal), carrying the rule id so the server re-checks it and persists the
 *   source (forecast_category_suggestion) — the audit can tell an accepted
 *   suggestion from a rep's own call.
 * - THE RULE IS THE SERVER'S. Every row, pill and reason is the wire's
 *   `suggestions` array (F1, forecast.Suggest), rendered in the wire's order
 *   (effective amount descending, uncaptured last — the spec's sort). Nothing
 *   here evaluates a rule.
 * - ACCEPT ALL IS NOT BUILT — REFUSED (ADOPTION §18.6): one click
 *   categorising every pending deal by rule is the derivation P2-9 forbids,
 *   and it would be the platform's first multi-record mutation, invented for
 *   a convenience button. The spec's "Accept all {n}" button is deliberately
 *   absent, not forgotten. Revisiting it needs the three conditions §18.6
 *   records (P2-9 amended with a persisted source, a multi-record mutation
 *   that exists for a stronger reason, the rule versioned server-side).
 * - "LIVE RECALCULATION" IS A REFETCH of /v1/sales/forecast after each write.
 *   The swap to "✓ Set" happens when the server has CONFIRMED the write, not
 *   before (the spec says optimistic; a "✓ Set" the server then refuses
 *   would be a sentence the data contradicts). An accepted row stays in the
 *   card as "✓ Set" after the refetch drops it from the wire, so the Undo
 *   has a row to stand on; Undo is the ordinary clearForecastCategory.
 */
import type { SalesForecast, SalesForecastSuggestion } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { SLUG_TO_STAGE } from '../../api'
import { fmtMoney } from './SalesDashboard'
import { forecastCategoryLabel } from './vocab'

/** The card's subtitle's fixed half — one literal, so it survives the build. */
export const SUGGESTIONS_BASIS = 'Suggestions are based on stage and close date.'
/** How long the Undo toast offers its action (spec §3.3). */
export const UNDO_MS = 5000

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const utc = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

export interface SuggestionRow {
  dealId: string
  title: string
  company: string
  /** The Deal cell's sub-line: the close date, or the absence of one. */
  closeLine: string
  /** Rule 2 (no close date): the sub-line turns warning (spec §3.3). */
  closeWarn: boolean
  /** The linked employee's name, or null when no employee is linked. */
  ownerName: string | null
  stageLabel: string
  /** null = the value is not captured in GBP (non-GBP, no capture). */
  pence: number | null
  valueText: string
  valueExact: string
  category: string
  categoryLabel: string
  ruleId: number
  reason: string
}

/** "Closes 30 Sep" / "Was due 12 Sep" / "No close date"; the year is named
 *  when it is not the server's current year. `today` is the wire's. */
export function closeLine(expectedClose: string | undefined, today: string): string {
  if (!expectedClose) return 'No close date'
  const c = utc(expectedClose)
  const d = new Date(c)
  const year = d.getUTCFullYear() === new Date(utc(today)).getUTCFullYear() ? '' : ` ${d.getUTCFullYear()}`
  const date = `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]}${year}`
  return c < utc(today) ? `Was due ${date}` : `Closes ${date}`
}

export function suggestionRow(s: SalesForecastSuggestion, today: string): SuggestionRow {
  return {
    dealId: s.dealId,
    title: s.title,
    company: s.company,
    closeLine: closeLine(s.expectedClose, today),
    closeWarn: !s.expectedClose,
    ownerName: s.employeeName ? s.employeeName : null,
    stageLabel: SLUG_TO_STAGE[s.stage] ?? s.stage,
    pence: s.pence,
    valueText: s.pence === null ? '—' : fmtMoney(s.pence),
    valueExact: s.pence === null ? 'Value not captured in GBP' : money(s.pence / 100),
    category: s.suggestedCategory,
    categoryLabel: forecastCategoryLabel(s.suggestedCategory),
    ruleId: s.ruleId,
    reason: s.reason,
  }
}

/** The wire's pending rows, or null for a response that predates F1 (the
 *  card then renders nothing rather than an empty table). */
export function suggestionRows(f: SalesForecast): SuggestionRow[] | null {
  if (!f.suggestions || !f.today) return null
  return f.suggestions.map(s => suggestionRow(s, f.today!))
}

/** "{n} deals have no forecast category. …" — n is the wire's pending count:
 *  every open deal with no effective category carries a suggestion (the
 *  rules cover every open stage), so the two populations are one. */
export function suggestionsSubtitle(pending: number): string {
  const head = pending === 0
    ? 'Every open deal has a forecast category.'
    : `${pending} ${pending === 1 ? 'deal has' : 'deals have'} no forecast category.`
  return `${head} ${SUGGESTIONS_BASIS}`
}

/** The rows the card shows after a refetch: the wire's pending rows, plus
 *  every row this session accepted (which the wire no longer lists), each in
 *  the position it held before. No sort is made here — the wire's order
 *  stands, and a row the wire newly lists joins at the end. */
export function mergeRows(prev: SuggestionRow[], wire: SuggestionRow[], acceptedIds: ReadonlySet<string>): SuggestionRow[] {
  const fromWire = new Map(wire.map(r => [r.dealId, r]))
  const out: SuggestionRow[] = []
  const seen = new Set<string>()
  for (const r of prev) {
    const w = fromWire.get(r.dealId)
    if (w) { out.push(w); seen.add(r.dealId) } else if (acceptedIds.has(r.dealId)) { out.push(r); seen.add(r.dealId) }
  }
  for (const w of wire) if (!seen.has(w.dealId)) out.push(w)
  return out
}

/** The toast after a confirmed accept. */
export function acceptedToast(r: SuggestionRow): string {
  return `"${r.title}" set to ${r.categoryLabel}`
}

/** The toast after a confirmed undo. */
export function undoneToast(r: SuggestionRow): string {
  return `"${r.title}" is Uncategorised again`
}
